import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Button, DropdownSelect, Icon, Loading, Modal, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useInviteNetworkMutation } from "@/redux/apis/network/networkApi";
import { addToast } from "@/utils";
import { PrivateNetworkStep } from "../types";
import { InviteWarningStep } from "./InviteWarningStep";

type FormData = {
  companyCode: string;
  tier: string;
};

interface InviteNetworkCodeModalProps {
  open: boolean;
  onClose: () => void;
  setStep: (step: PrivateNetworkStep) => void;
}

export const InviteNetworkCodeModal = ({ open, onClose, setStep }: InviteNetworkCodeModalProps) => {
  const [inviteNetwork, { isLoading }] = useInviteNetworkMutation();
  const { register, handleSubmit, watch, setValue } = useForm<FormData>({ defaultValues: { tier: "3" } });
  const [internalStep, setInternalStep] = useState<"warning" | "form">("warning");

  useEffect(() => {
    if (open) setInternalStep("warning");
  }, [open]);

  const onSubmit = (data: FormData) => {
    if (!data) return;

    inviteNetwork({ provider_code: data.companyCode, tier: Number(data.tier) })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Sent an Invitation");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  const isWarning = internalStep === "warning";

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-small"
      featuredIcon={
        isWarning ? (
          <div className="flex items-center gap-2">
            <Icon name="Danger" variant="Bold" className="text-warning" size="xl" />
            <Typography variant="title">Before you send an invitation</Typography>
          </div>
        ) : undefined
      }
      title={isWarning ? undefined : "Invite to Join Private Network"}
      description={
        isWarning
          ? "Private network invitations should only be sent to operators you have an established working relationship with."
          : "Once you invite your trusted partner(s) to join your private network, the invitation will only become active once they accept."
      }
      headerClassName={undefined}
      renderButtons={() =>
        isWarning ? (
          <>
            <Button variant="secondary" size="lg" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button variant="primary" size="lg" onClick={() => setInternalStep("form")} className="flex-1">
              Yes, Continue
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" size="lg" onClick={handleSubmit(onSubmit)}>
              Continue
            </Button>
          </>
        )
      }
    >
      {isLoading && <Loading />}
      {isWarning ? (
        <InviteWarningStep />
      ) : (
        <div className="flex w-full flex-col gap-3">
          <Alert
            type="info"
            message={
              <>
                Business or Company you&apos;re trying to invite is not a member of RideMinder yet?{" "}
                <span className="cursor-pointer underline" onClick={() => setStep("inviteEmail")}>
                  Click here to send them an email invitation
                </span>
              </>
            }
          />
          <TextInput placeholder="Transport Provider Code" className="w-full" maxLength={32} required {...register("companyCode")} />
          <div className="flex flex-col gap-1">
            <Typography variant="action">Operator Tier</Typography>
            <DropdownSelect
              options={[
                { name: "Tier 1", value: "1" },
                { name: "Tier 2", value: "2" },
                { name: "Tier 3", value: "3" },
              ]}
              value={watch("tier")}
              onChange={(val: string) => setValue("tier", val)}
            />
            <Typography variant="small-regular" className="text-neutral-dark-gray">
              Tier determines the priority of this provider when dispatching jobs. Tier 1 is the highest priority.
            </Typography>
          </div>
        </div>
      )}
    </Modal>
  );
};
